import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { HIST } from "@/constants/testIds";
import {
  Trash2, Search, FileText, Link2, Upload, Sparkles, Camera, BookMarked, WifiOff,
  FolderOpen,
} from "lucide-react";
import { ReadingListView } from "@/pages/ReadingList";
import { cacheList, loadCachedList, forgetDetail } from "@/lib/summariesCache";

const sourceIcon = (s) =>
  s === "url"
    ? Link2
    : s === "file"
    ? Upload
    : s === "book_scan"
    ? Camera
    : s === "dossier"
    ? FolderOpen
    : FileText;

const TABS = { HISTORY: "history", READING_LIST: "reading-list" };

export default function History() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === TABS.READING_LIST ? TABS.READING_LIST : TABS.HISTORY;
  const [tab, setTab] = useState(initialTab);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fromCache, setFromCache] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/summaries");
      setItems(data);
      setFromCache(false);
      // Persist for offline access on the next visit.
      cacheList(data);
    } catch (err) {
      // Network / server error → try the offline cache before giving up.
      const cached = await loadCachedList();
      if (cached && cached.items.length) {
        setItems(cached.items);
        setFromCache(true);
        toast.info(t("history.offline_cache"));
      } else {
        toast.error(t("history.load_failed"));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Keep URL in sync with tab so /history?tab=reading-list is bookmarkable.
  useEffect(() => {
    const currentTab = searchParams.get("tab");
    if (tab === TABS.HISTORY && currentTab) {
      setSearchParams({}, { replace: true });
    } else if (tab === TABS.READING_LIST && currentTab !== TABS.READING_LIST) {
      setSearchParams({ tab: TABS.READING_LIST }, { replace: true });
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((it) => it.title.toLowerCase().includes(needle));
  }, [items, q]);

  const onDelete = async (id) => {
    if (!window.confirm(t("history.confirm_delete"))) return;
    try {
      await api.delete(`/summaries/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      // Also drop the cached detail so it doesn't reappear offline.
      forgetDetail(id);
      // Refresh the cached list without the deleted item.
      cacheList(items.filter((x) => x.id !== id));
      toast.success(t("history.deleted_toast"));
    } catch {
      toast.error(t("history.delete_failed"));
    }
  };

  const tabButtonClasses = (isActive) =>
    `px-5 py-3 font-mono uppercase tracking-[0.2em] text-[11px] border-b-2 transition-colors flex items-center gap-2 ${
      isActive
        ? "border-[#002FA7] text-[#002FA7] font-semibold"
        : "border-transparent text-muted-foreground hover:text-foreground"
    }`;

  return (
    <div className="border-t border-border">
      {/* Shared header */}
      <div className="px-6 md:px-10 py-10 border-b border-border">
        <div className="sux-marker mb-3">{t("history.marker")}</div>
        <h1
          className="text-4xl md:text-5xl font-black tracking-tight"
          style={{ fontFamily: "Outfit" }}
        >
          {tab === TABS.HISTORY ? t("history.title") : t("reading_list.page_title")}
        </h1>
        <p className="text-muted-foreground mt-3 max-w-2xl text-sm">
          {tab === TABS.HISTORY
            ? t("history.subtitle")
            : t("reading_list.page_subtitle")}
        </p>
      </div>

      {/* Tab bar */}
      <div className="px-6 md:px-10 border-b border-border flex items-center gap-2 overflow-x-auto">
        <button
          data-testid="history-tab-summaries"
          className={tabButtonClasses(tab === TABS.HISTORY)}
          onClick={() => setTab(TABS.HISTORY)}
        >
          <Sparkles className="w-3.5 h-3.5" />
          {t("nav.history")}
          <span className="ml-1 text-[10px] opacity-70">({items.length})</span>
        </button>
        <button
          data-testid="history-tab-reading-list"
          className={tabButtonClasses(tab === TABS.READING_LIST)}
          onClick={() => setTab(TABS.READING_LIST)}
        >
          <BookMarked className="w-3.5 h-3.5" />
          {t("reading_list.nav")}
        </button>
      </div>

      {/* Panels */}
      {tab === TABS.HISTORY ? (
        <>
          {fromCache && (
            <div
              data-testid="history-cache-banner"
              className="px-6 md:px-10 py-2 border-b border-border bg-[#002FA7]/5 flex items-center gap-2 font-mono uppercase tracking-[0.15em] text-[10px] text-[#002FA7]"
            >
              <WifiOff className="w-3 h-3" />
              {t("history.offline_cache")}
            </div>
          )}
          <div className="px-6 md:px-10 py-4 border-b border-border flex items-center gap-4">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("history.search_placeholder")}
              data-testid={HIST.searchInput}
              className="flex-1 bg-transparent border-0 focus:outline-none font-mono text-sm placeholder:text-muted-foreground"
            />
            <span className="sux-marker">{t("history.entries", { count: filtered.length })}</span>
          </div>

          <div data-testid={HIST.list}>
            {loading ? (
              <div className="px-6 md:px-10 py-16 sux-marker">{t("history.loading")}</div>
            ) : filtered.length === 0 ? (
              <div data-testid={HIST.empty} className="px-6 md:px-10 py-24 text-center">
                <Sparkles className="w-8 h-8 text-[#002FA7] mx-auto mb-4" />
                <h3 className="text-2xl font-black" style={{ fontFamily: "Outfit" }}>
                  {q ? t("history.no_match") : t("history.empty_title")}
                </h3>
                <p className="text-muted-foreground mt-3 text-sm">
                  {q ? t("history.no_match_help") : t("history.empty_help")}
                </p>
                {!q && (
                  <Link
                    to="/"
                    className="inline-block mt-6 bg-[#002FA7] text-white font-mono uppercase tracking-[0.25em] text-[11px] px-6 py-3 hover:bg-[#00227A] transition-colors"
                  >
                    {t("history.new_summary")}
                  </Link>
                )}
              </div>
            ) : (
              filtered.map((it) => {
                const Icon = sourceIcon(it.source_type);
                const date = new Date(it.created_at);
                return (
                  <Link
                    key={it.id}
                    to={`/history/${it.id}`}
                    data-testid={HIST.item(it.id)}
                    className="group grid grid-cols-12 items-center gap-4 px-6 md:px-10 py-5 border-b border-border hover:bg-secondary/60 transition-colors"
                  >
                    <div className="col-span-3 md:col-span-2 sux-marker">
                      {date.toLocaleDateString(undefined, {
                        year: "numeric", month: "short", day: "2-digit",
                      })}
                      <br />
                      <span className="text-[10px]">
                        {date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                    <div className="col-span-9 md:col-span-6 flex items-center gap-3 min-w-0">
                      <Icon
                        className={`w-4 h-4 flex-shrink-0 ${
                          it.source_type === "book_scan" ? "text-[#FF2A00]" : "text-[#002FA7]"
                        }`}
                      />
                      <span className="font-medium text-base truncate group-hover:text-[#002FA7]">
                        {it.title}
                      </span>
                      {it.source_type === "book_scan" && (
                        <span className="font-mono uppercase tracking-widest text-[9px] bg-[#FF2A00] text-white px-1.5 py-0.5 flex-shrink-0 font-bold">
                          {t("history.badge_book")}
                        </span>
                      )}
                      {it.is_public && (
                        <span className="font-mono uppercase tracking-widest text-[9px] border border-[#002FA7] text-[#002FA7] px-1.5 py-0.5 flex-shrink-0">
                          {t("history.badge_public")}
                        </span>
                      )}
                    </div>
                    <div className="hidden md:flex col-span-3 items-center justify-end gap-2 flex-wrap">
                      <span className="font-mono uppercase tracking-widest text-[10px] border border-border px-2 py-1">
                        {it.model.replace("claude-sonnet-4-5", "claude").replace("gemini-3-flash", "gemini")}
                      </span>
                      <span className="font-mono uppercase tracking-widest text-[10px] border border-border px-2 py-1">
                        {it.length}
                      </span>
                    </div>
                    <div className="col-span-12 md:col-span-1 flex justify-end">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          onDelete(it.id);
                        }}
                        data-testid={HIST.deleteItem(it.id)}
                        className="p-2 text-muted-foreground hover:text-[#FF2A00] transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </>
      ) : (
        <ReadingListView embedded={true} />
      )}
    </div>
  );
}
